import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * <caldera-subscription>
 *
 * Subscribe-and-save selector for the buy box. Two cards — "Subscribe & Save"
 * (with a frequency button group) and "Buy Once" — drive a hidden
 * `selling_plan` input on Horizon's product form, so the choice flows through
 * the existing cart / checkout / Shop Pay with no Recharge widget.
 *
 * - Subscribe selected  → selling_plan = the active frequency button's plan id
 * - Buy Once selected    → selling_plan disabled (one-time purchase)
 * - Clicking a frequency button selects it and re-activates Subscribe.
 *
 * The sticky add-to-cart bar's price and the add-to-cart button's price suffix
 * both mirror the selected card's price, so they show the subscription price
 * when subscribe is active and the one-time price when Buy Once is active.
 *
 * On a variant change the buy box doesn't re-render, so we morph our options
 * from the re-fetched page markup carried by VariantUpdateEvent.
 *
 * Every sync announces the selected path as a bubbling
 * `caldera:subscription:change` event, which caldera-sold-out-notice.js uses to
 * swap Add to cart for Notify Me on a subscription-required product.
 *
 * @typedef {object} CalderaSubscriptionRefs
 * @property {HTMLElement} content - The options container that gets morphed on variant change.
 * @extends {Component<CalderaSubscriptionRefs>}
 */
class CalderaSubscription extends Component {
  requiredRefs = ['content'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#syncSellingPlan);
    this.addEventListener('click', this.#onFrequencyClick);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#syncSellingPlan();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#syncSellingPlan);
    this.removeEventListener('click', this.#onFrequencyClick);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * The scope this instance resolves DOM in: the quick-add modal when we're
   * rendered inside it, otherwise the whole document. Scoping is essential
   * because a quick-buy modal opened over a PDP renders a second buy box whose
   * section id — and therefore form id and add-to-cart button — collides with
   * the PDP behind it. A document-wide lookup would resolve the PDP's form and
   * button instead of the modal's, so the modal's price never updates.
   * @returns {Document | Element}
   */
  #scope() {
    return this.closest('#quick-add-dialog') ?? document;
  }

  /**
   * Finds the product form to write the selling plan into. Prefers the explicit
   * form id (product page); falls back to the product-form-component matching
   * this block's product id, so it also works inside the quick-add modal. Both
   * lookups are scoped to #scope() so the modal never resolves the PDP's form.
   * @returns {HTMLFormElement | null}
   */
  #form() {
    const { formId, productId } = this.dataset;
    const scope = this.#scope();

    if (formId) {
      const byId = scope.querySelector(`#${CSS.escape(formId)}`);
      if (byId instanceof HTMLFormElement) return byId;
    }

    for (const component of scope.querySelectorAll('product-form-component')) {
      if (productId && component.getAttribute('data-product-id') !== productId) continue;
      const form = component.querySelector('form');
      if (form instanceof HTMLFormElement) return form;
    }

    return null;
  }

  /** @returns {string} The plan id of the active frequency button. */
  #activePlanId() {
    const active =
      this.querySelector('[data-freq-button].is-active') ?? this.querySelector('[data-freq-button]');
    return active instanceof HTMLElement ? active.dataset.planId ?? '' : '';
  }

  /** @returns {boolean} Whether the subscribe option is currently selected. */
  #isSubscribe() {
    const subscribe = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="subscribe"]'));
    return subscribe?.checked ?? false;
  }

  /** Mirrors the current choice into a hidden `selling_plan` input on the product form. */
  #syncSellingPlan = () => {
    const form = this.#form();
    if (!form) return;

    let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="selling_plan"]'));
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }

    const value = this.#isSubscribe() ? this.#activePlanId() : '';
    input.value = value;
    // Empty value = one-time purchase: disable so it isn't submitted as a blank plan.
    input.disabled = value === '';

    this.#updateStickyPrice();
    this.#updateButtonPrice();

    // A radio change bubbles natively, but a frequency click re-checks
    // Subscribe programmatically and fires nothing — so every path announces
    // itself here instead.
    this.dispatchEvent(
      new CustomEvent('caldera:subscription:change', { bubbles: true, detail: { subscribe: this.#isSubscribe() } })
    );
  };

  /**
   * Mirrors the selected card's price (subscribe vs. one-time) into the
   * add-to-cart button's price suffix within this block's product form, so the
   * CTA shows the price for the chosen plan. Reads the card's already-rendered
   * (presentment-currency) price and drops the struck-through compare-at, so it
   * stays market-correct. No-op when the button/price suffix isn't present.
   */
  #updateButtonPrice = () => {
    const form = this.#form();
    if (!form) return;

    const priceSuffixes = form.querySelectorAll('.caldera-atc-price');
    if (!priceSuffixes.length) return;

    const role = this.#isSubscribe() ? 'subscribe' : 'onetime';
    const source = this.querySelector(`.caldera-subscription__card--${role} .caldera-subscription__price`);
    if (!source) return;

    // Active price only — clone so we can strip the compare-at without touching the card.
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.querySelector('.caldera-subscription__compare')?.remove();
    const price = clone.textContent?.trim();
    if (!price) return;

    for (const suffix of priceSuffixes) {
      suffix.textContent = `  ${price}`;
    }
  };

  /**
   * Mirrors the selected card's price into the sticky add-to-cart bar so the
   * floating bar reflects subscribe vs. one-time pricing. No-op when the sticky
   * bar isn't rendered. Runs after the sticky bar's own variant morph (which
   * bubbles to the section before this document-level listener), so it wins.
   */
  #updateStickyPrice = () => {
    const target = this.#scope().querySelector('.sticky-add-to-cart__price');
    if (!target) return;

    const role = this.#isSubscribe() ? 'subscribe' : 'onetime';
    const source = this.querySelector(`.caldera-subscription__card--${role} .caldera-subscription__price`);
    if (!source) return;

    target.innerHTML = source.innerHTML;
  };

  /** @param {Event} event */
  #onFrequencyClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('[data-freq-button]');
    if (!(button instanceof HTMLElement)) return;

    for (const other of this.querySelectorAll('[data-freq-button]')) {
      const isActive = other === button;
      other.classList.toggle('is-active', isActive);
      other.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    // Choosing a frequency implies subscribing.
    const subscribe = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-role="subscribe"]'));
    if (subscribe) subscribe.checked = true;

    this.#syncSellingPlan();
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ data?: { html?: Document, productId?: string } }} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    const html = detail?.data?.html;
    if (!html) return;

    const { productId } = detail.data ?? {};
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const incoming =
      html.querySelector(`caldera-subscription[data-block-id="${this.dataset.blockId}"] [ref="content"]`) ??
      html.querySelector('caldera-subscription [ref="content"]');

    // The fetched markup carries the server default (subscribe + popular
    // frequency), so capture the shopper's selection and re-apply it after the
    // morph — otherwise a variant switch silently reverts their choice.
    const wasSubscribe = this.#isSubscribe();
    const previousPlanId = this.#activePlanId();

    if (incoming) {
      morph(this.refs.content, incoming);
    }

    const role = wasSubscribe ? 'subscribe' : 'onetime';
    const radio = /** @type {HTMLInputElement | null} */ (this.querySelector(`[data-role="${role}"]`));
    if (radio) radio.checked = true;

    const buttons = this.querySelectorAll('[data-freq-button]');
    const match = [...buttons].find(
      (button) => button instanceof HTMLElement && button.dataset.planId === previousPlanId
    );
    if (match) {
      for (const button of buttons) {
        const isActive = button === match;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    }

    this.#syncSellingPlan();
  };
}

customElements.define('caldera-subscription', CalderaSubscription);
